import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Bell, CheckCircle2, Megaphone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BroadcastPopup - Mostra notifiche broadcast come popup bloccante
 * 
 * Features:
 * - Mostra popup bloccante per broadcast non letti
 * - Processa un broadcast alla volta
 * - Marca come letto quando l'utente clicca "Letto"
 * - Espone il contatore per il badge via callback
 */
export const BroadcastPopup = ({ onUnreadCountChange }) => {
  const { token, user } = useAuth();
  const [broadcasts, setBroadcasts] = useState([]);
  const [currentBroadcast, setCurrentBroadcast] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isMarking, setIsMarking] = useState(false);

  // Fetch broadcasts non letti
  const fetchUnreadBroadcasts = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/broadcasts/unread`, {
      });
      
      const unreadBroadcasts = response.data.broadcasts || [];
      setBroadcasts(unreadBroadcasts);
      
      // Notifica il contatore al parent
      if (onUnreadCountChange) {
        onUnreadCountChange(unreadBroadcasts.length);
      }
      
      // Se ci sono broadcast non letti, mostra il primo
      if (unreadBroadcasts.length > 0 && !currentBroadcast) {
        setCurrentBroadcast(unreadBroadcasts[0]);
        setIsOpen(true);
      }
    } catch (error) {
      console.error('Errore fetch broadcasts:', error);
    }
  }, [token, onUnreadCountChange, currentBroadcast]);

  // Fetch al mount e ogni 30 secondi per real-time
  useEffect(() => {
    fetchUnreadBroadcasts();
    
    const interval = setInterval(fetchUnreadBroadcasts, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadBroadcasts]);

  // Marca come letto
  const handleMarkRead = async () => {
    if (!currentBroadcast || isMarking) return;
    
    setIsMarking(true);
    try {
      await axios.post(
        `${API}/broadcasts/${currentBroadcast.id}/mark-read`,
        {}
      );
      
      // Rimuovi dalla lista locale
      const remaining = broadcasts.filter(b => b.id !== currentBroadcast.id);
      setBroadcasts(remaining);
      
      // Notifica il nuovo contatore
      if (onUnreadCountChange) {
        onUnreadCountChange(remaining.length);
      }
      
      // Se ci sono altri broadcast, mostra il prossimo
      if (remaining.length > 0) {
        setCurrentBroadcast(remaining[0]);
      } else {
        setCurrentBroadcast(null);
        setIsOpen(false);
      }
    } catch (error) {
      console.error('Errore mark-read:', error);
    } finally {
      setIsMarking(false);
    }
  };

  // Chiudi il popup e marca come letto
  const handleClose = async () => {
    if (currentBroadcast && !isMarking) {
      await handleMarkRead();
    }
  };

  // Non mostrare nulla se non c'è broadcast
  if (!currentBroadcast) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        handleClose();
      }
    }}>
      <DialogContent 
        className="sm:max-w-lg"
        data-testid="broadcast-popup"
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Megaphone className="w-5 h-5 text-amber-600" />
            </div>
            <DialogTitle className="text-lg font-semibold text-slate-800">
              {currentBroadcast.title}
            </DialogTitle>
          </div>
          {broadcasts.length > 1 && (
            <p className="text-xs text-slate-500">
              Messaggio 1 di {broadcasts.length}
            </p>
          )}
        </DialogHeader>
        
        <DialogDescription asChild>
          <div className="py-4">
            <div 
              className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentBroadcast.content.replace(/\n/g, '<br/>'), { ALLOWED_TAGS: ['br', 'b', 'strong', 'i', 'em', 'u', 'a', 'p', 'span'], ALLOWED_ATTR: ['href', 'target', 'rel'] }) }}
            />
            
            {currentBroadcast.created_by_name && (
              <p className="mt-4 text-xs text-slate-400">
                Inviato da: {currentBroadcast.created_by_name}
              </p>
            )}
          </div>
        </DialogDescription>
        
        <DialogFooter>
          <Button 
            onClick={handleMarkRead}
            disabled={isMarking}
            className="w-full sm:w-auto"
            data-testid="broadcast-mark-read-btn"
          >
            {isMarking ? (
              'Caricamento...'
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Ho letto
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * BroadcastBadge - Badge rosso per notifiche non lette
 * Usato nella sidebar per indicare messaggi da leggere
 */
export const BroadcastBadge = ({ count }) => {
  if (!count || count === 0) return null;
  
  return (
    <span 
      className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse"
      data-testid="broadcast-badge"
    >
      {count > 9 ? '9+' : count}
    </span>
  );
};

export default BroadcastPopup;
